import usePosOrderDeliveryCost from "@/shared/atoms/usePosOrderDeliveryCostAtom";
import usePosOrderHandOverDate from "@/shared/atoms/usePosOrderHandOverDateAtom";
import usePosOrderPayment from "@/shared/atoms/usePosOrderPaymentAtom";
import useBranchId from "@/shared/hooks/useBranchId";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import { DeliveryMethods } from "@/shared/utils/enum";
import { convertNullToUndefinedInObject } from "@/shared/utils/helpers";
import usePosOrderCustomDiscount from "../atoms/usePosOrderCustomDiscount";
import usePosOrderDeliveryMethod from "../atoms/usePosOrderDeliveryMethodAtom";
import usePosOrderRemarkAtom from "../atoms/usePosOrderRemarkAtom";
import usePosOrderTable from "../atoms/usePosOrderTable";
import usePosOrderUser from "../atoms/usePosOrderUser";
import usePosTakeout from "../atoms/usePosTakeout";
import {
  CartItem,
  PaymentLedger,
  PaymentMethodIdType,
} from "../utils/interfaces";
import useCategoryQueryHook from "./useCategoryQueryHook";
import useDiscountCodeHook from "./useDiscountCodeHook";
import { useHasSession, useSessionId } from "./usePosOrderTableSessionHook";
import usePosOrderTipHook from "./usePosOrderTipHook";
import usePosOrderTotalAmountHook from "./usePosOrderTotalAmountHook";
import { useCart } from "./useCartHook";
import usePaymentHook from "./usePaymentHook";

interface SubmitDataProps {
  onTheFlyPrinting?: boolean;
  customCart?: CartItem[];
  ledger?: PaymentLedger;
  customPaymentId?: PaymentMethodIdType;
}

const usePosOrderSubmitDataHook = () => {
  const [isTakeOut] = usePosTakeout();
  const branch = useBranchId();
  const hasSession = useHasSession();
  const sessionId = useSessionId();
  const [cart] = useCart();
  const [customDiscount] = usePosOrderCustomDiscount();
  const { isOrderEditing, editOrder } = useIsOrderEditingHook();
  const [deliveryMethod] = usePosOrderDeliveryMethod();
  const [table] = usePosOrderTable();
  const [paymentId] = usePosOrderPayment();
  const [date] = usePosOrderHandOverDate();
  const [deliveryCost] = usePosOrderDeliveryCost();
  const [remark] = usePosOrderRemarkAtom();
  const { discountCode } = useDiscountCodeHook();
  const [orderUser] = usePosOrderUser();
  const { tipValue } = usePosOrderTipHook();
  const { products } = useCategoryQueryHook();
  const newOrderUser = convertNullToUndefinedInObject(orderUser || {});
  const { time, isAsap = false } = date || {};
  const isQr = deliveryMethod === DeliveryMethods.DINE_IN;
  const totalAmount = usePosOrderTotalAmountHook();
  const { isOnlinePayment } = usePaymentHook();

  const retOrderUser = { ...newOrderUser, item_id: undefined };

  const retOrderInformation = () => ({
    order_session_id: hasSession ? sessionId : undefined,
    table_number: isQr ? table?.table_number : undefined,
    delivery_cost: deliveryCost || "0",
    delivery_method_id: isTakeOut ? DeliveryMethods.TAKEOUT : deliveryMethod,
    payment_method_id: paymentId,
    source: 19,
    is_online_link_payment: isOnlinePayment(paymentId) || undefined,
    remarks: remark || undefined,
    branch_id: branch,
    loyalty_ledger_points: 0,
    hand_over_is_asap: isAsap,
    hand_over_time: isAsap ? undefined : time,
    ...(customDiscount && { ...customDiscount, type: undefined }),
  });

  const retOrderInformationForEditing = () => ({
    order_session_id: hasSession ? sessionId : undefined,
    table_number: isQr ? table?.table_number : undefined,
    delivery_cost: deliveryCost || "0",
    delivery_method_id: isTakeOut ? DeliveryMethods.TAKEOUT : deliveryMethod,
    payment_method_id: paymentId,
    source: 19,
    is_online_link_payment: isOnlinePayment(paymentId) || undefined,
    remarks: remark || "",
    ...(customDiscount && { ...customDiscount, type: undefined }),
  });

  const retCart = (customCart) => {
    return (customCart ?? cart).map((item) => {
      const isCustomCartItem = item.item_id < 0;
      return {
        note: item.note,
        menu_item_id: isCustomCartItem ? undefined : item.item_id,
        menu_item_price: isCustomCartItem ? item.price : undefined,
        name: item.name,
        parent_id: item.parent_id,
        quantity: item.quantity,
        vat_percentage: item.vat_percentage,
        sub_content: item?.selected_sub_content?.map((sub) => ({
          ...sub,
          item_id: undefined,
          menu_item_id: sub.item_id,
        })),
      };
    });
  };

  const retCartForOnTheFlyPrinting = (customCart) => {
    return (customCart ?? cart).map((item) => {
      const product = products?.find((p) => p.item_id === item.item_id);
      const isCustomCartItem = item.item_id < 0;
      return {
        note: item.note,
        name: item.name,
        menu_item_id: isCustomCartItem ? undefined : item.item_id,
        menu_item_price: isCustomCartItem ? item.price : undefined,
        single_price_inc_vat: item.price,
        parent_name: product?.parent_name,
        quantity: item.quantity,
        vat_percentage: item.vat_percentage,
        sub_content: item?.selected_sub_content?.map((sub) => ({
          ...sub,
          item_id: undefined,
          menu_item_id: sub.item_id,
        })),
      };
    });
  };

  return ({
    onTheFlyPrinting,
    customCart,
    ledger,
    customPaymentId,
  }: SubmitDataProps = {}) => ({
    orderId: isOrderEditing ? editOrder?.order_information?.item_id : undefined,
    order_information: {
      ...(isOrderEditing
        ? retOrderInformationForEditing()
        : retOrderInformation()),
      total_price: onTheFlyPrinting
        ? (ledger?.payment_amount ?? totalAmount)
        : undefined,
      tip: onTheFlyPrinting ? (ledger?.payment_tip ?? tipValue) : tipValue,
      payment_method_id: customPaymentId ?? paymentId,
    },
    cart: onTheFlyPrinting
      ? retCartForOnTheFlyPrinting(customCart)
      : retCart(customCart),
    order_user: retOrderUser,
    discount_code_value: discountCode?.code || undefined,
  });
};

export default usePosOrderSubmitDataHook;
